/**
 * useNewCoinRealtime Hook
 *
 * Subscribes to `launched_tokens` INSERT events globally and invalidates
 * the home-grid, trending carousel, homepage (search panel), and feed
 * caches so a newly-launched coin renders across every connected client
 * within ~1 second.
 *
 * Throttle: leading-edge (fires immediately on first insert, coalesces
 * subsequent inserts within a 500ms window). Trailing debounce was
 * rejected because it adds the debounce window to every update path.
 *
 * Does NOT call clearPoolSnapshotCache(): that utility is a global wipe
 * and would thundering-herd the market-data /tv/pool-snapshot endpoint.
 * useAllTokensWithPoolInfo has an RPC fallback for pools missing from
 * the snapshot, so the new coin renders correctly during the 10s TTL.
 *
 * Diagnostic telemetry: tagged breadcrumbs and a per-session
 * captureMessage baseline let Sentry passively categorize a Chrome iOS
 * regression without requiring Remote Web Inspector. Remove once root
 * cause is fixed.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useVisibilityReconnect } from '@/providers/VisibilityReconnectProvider';
import { captureError, addBreadcrumb, Sentry } from '@/lib/sentry';
import { isIOSChrome, isMobileSafari } from '@/lib/storage/cookieStorage';

const DEBUG = import.meta.env.DEV;

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;
const THROTTLE_MS = 500;
const MAX_INSERT_BREADCRUMBS = 20;

type Browser = 'chrome_ios' | 'safari_ios' | 'other';

function detectBrowser(): Browser {
  if (isIOSChrome()) return 'chrome_ios';
  if (isMobileSafari()) return 'safari_ios';
  return 'other';
}

function diagnosticTags() {
  return {
    browser: detectBrowser(),
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : 'unknown',
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : 'unknown',
  };
}

export function useNewCoinRealtime() {
  const queryClient = useQueryClient();
  const { wakeCounter } = useVisibilityReconnect();

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFireRef = useRef(0);
  const exhaustedRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  const insertBreadcrumbCountRef = useRef(0);
  const subscribedCapturedRef = useRef(false);

  const fireInvalidations = useCallback(() => {
    addBreadcrumb('new_coin_realtime INVALIDATIONS_FIRED', 'realtime', diagnosticTags());
    queryClient.invalidateQueries({ queryKey: ['all-tokens-with-pool-info'] });
    queryClient.invalidateQueries({ queryKey: ['trending-tokens'] });
    queryClient.invalidateQueries({ queryKey: ['feed-tokens'] });
    queryClient.invalidateQueries({ queryKey: ['homepage-tokens'] });
  }, [queryClient]);

  const cleanup = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  const subscribe = useCallback(() => {
    if (retryCountRef.current >= MAX_RETRIES) {
      return;
    }

    const channelName = `launched-tokens-${Date.now()}`;
    DEBUG && console.log('[useNewCoinRealtime] Subscribing to', channelName, `(attempt ${retryCountRef.current + 1})`);

    channelRef.current = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'launched_tokens',
        },
        (payload: { new?: Record<string, unknown> }) => {
          DEBUG && console.log('[useNewCoinRealtime] launched_tokens INSERT');

          if (insertBreadcrumbCountRef.current < MAX_INSERT_BREADCRUMBS) {
            insertBreadcrumbCountRef.current++;
            const mint =
              typeof payload?.new?.mint_address === 'string'
                ? (payload.new.mint_address as string).slice(0, 12)
                : 'unknown';
            addBreadcrumb('new_coin_realtime INSERT_RECEIVED', 'realtime', {
              ...diagnosticTags(),
              mint_address: mint,
              time_since_mount_ms: Date.now() - mountTimeRef.current,
            });
          }

          retryCountRef.current = 0;
          const now = Date.now();
          if (now - lastFireRef.current >= THROTTLE_MS) {
            lastFireRef.current = now;
            fireInvalidations();
          } else {
            addBreadcrumb('new_coin_realtime INSERT_THROTTLED', 'realtime', {
              ...diagnosticTags(),
              ms_since_last_fire: now - lastFireRef.current,
            });
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          DEBUG && console.log('[useNewCoinRealtime] Successfully subscribed');
          retryCountRef.current = 0;
          exhaustedRef.current = false;
          addBreadcrumb('new_coin_realtime SUBSCRIBED', 'realtime', {
            ...diagnosticTags(),
            attempt: retryCountRef.current,
          });
          if (!subscribedCapturedRef.current) {
            subscribedCapturedRef.current = true;
            Sentry.captureMessage('new_coin_realtime SUBSCRIBED (session baseline)', {
              level: 'info',
              tags: {
                feature: 'new_coin_realtime',
                rpc_provider: 'supabase_realtime',
                browser: detectBrowser(),
              },
              extra: {
                userAgent:
                  typeof navigator !== 'undefined'
                    ? navigator.userAgent.slice(0, 200)
                    : 'unknown',
                visibilityState:
                  typeof document !== 'undefined'
                    ? document.visibilityState
                    : 'unknown',
                time_since_mount_ms: Date.now() - mountTimeRef.current,
              },
            });
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[useNewCoinRealtime] Subscription failed:', status, err);
          captureError(err instanceof Error ? err : new Error(`new_coin_realtime ${status}`), {
            ...diagnosticTags(),
            feature: 'new_coin_realtime',
            rpc_provider: 'supabase_realtime',
            status,
            retryCount: retryCountRef.current,
          });
          retryCountRef.current++;
          if (retryCountRef.current < MAX_RETRIES) {
            retryTimeoutRef.current = setTimeout(() => {
              cleanup();
              subscribe();
            }, RETRY_DELAY_MS);
          } else if (!exhaustedRef.current) {
            exhaustedRef.current = true;
            captureError(
              new Error('new_coin_realtime retries exhausted, falling back to poll'),
              {
                ...diagnosticTags(),
                feature: 'new_coin_realtime',
                rpc_provider: 'supabase_realtime',
                status: 'retries_exhausted',
                retryCount: retryCountRef.current,
                lastStatus: status,
              }
            );
          }
        } else if (status === 'CLOSED') {
          addBreadcrumb('new_coin_realtime CLOSED', 'realtime', diagnosticTags());
        }
      });
  }, [cleanup, fireInvalidations]);

  useEffect(() => {
    retryCountRef.current = 0;
    exhaustedRef.current = false;
    if (wakeCounter === 0) {
      addBreadcrumb('new_coin_realtime MOUNT', 'realtime', diagnosticTags());
    } else {
      addBreadcrumb('new_coin_realtime wake re-subscribe', 'realtime', {
        ...diagnosticTags(),
        wakeCounter,
      });
    }
    subscribe();
    return cleanup;
  }, [subscribe, cleanup, wakeCounter]);
}
